import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronRight, 
  Search, 
  Anchor, 
  Ship, 
  Award, 
  Headphones,
  ArrowRight,
  Gauge,
  Truck,
  Clock,
  Shield,
  Phone,
  CheckCircle,
  Star,
  Zap,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import EngineCard from '@/components/EngineCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero carousel images - Real marine diesel engines
const heroImages = [
  'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/9oowaboq_Like%20New%20Caterpillar%20C32%201925HP%20Tier%203%20Diesel%20Marine%20Engine.jpg',
  'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/rnderby4_Caterpillar%203508DITA%201120HP%20Diesel%20Marine%20Engine.jpeg',
  'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/levb3zan_Cummins%20KTA50-M%201000HP%20Diesel%20Marine%20Engine.jpg',
  'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/7r3ngvss_Caterpillar%20C18%20ACERT%20671HP%20Tier%203%20Diesel%20Marine%20Engine.jpg',
  'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/2245smph_Caterpillar%203412C%20DITA%20720HP%20Diesel%20Marine%20Engine.jpeg',
];

// Featured engine showcase images (user-uploaded real engines)
const showcaseEngines = [
  {
    image: 'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/9oowaboq_Like%20New%20Caterpillar%20C32%201925HP%20Tier%203%20Diesel%20Marine%20Engine.jpg',
    title: 'Caterpillar C32 1925HP',
    brand: 'Caterpillar',
    condition: 'Like New',
    price: '$165,000'
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/rnderby4_Caterpillar%203508DITA%201120HP%20Diesel%20Marine%20Engine.jpeg',
    title: 'Caterpillar 3508 DITA 1120HP',
    brand: 'Caterpillar',
    condition: 'Rebuilt',
    price: '$75,000'
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/levb3zan_Cummins%20KTA50-M%201000HP%20Diesel%20Marine%20Engine.jpg',
    title: 'Cummins KTA50-M 1000HP',
    brand: 'Cummins',
    condition: 'Good Used',
    price: '$35,000'
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_nautical-engines/artifacts/7r3ngvss_Caterpillar%20C18%20ACERT%20671HP%20Tier%203%20Diesel%20Marine%20Engine.jpg',
    title: 'Caterpillar C18 ACERT 671HP',
    brand: 'Caterpillar',
    condition: 'Rebuilt',
    price: '$65,000'
  },
];

// Brand colors
const brandColors = {
  'Volvo Penta': '#003057',
  'Caterpillar': '#FFCD00',
  'Cummins': '#E31937',
  'MAN': '#E40521',
  'Yanmar': '#E60012',
  'Mercury': '#000000',
  'MTU': '#004B87',
  'Wärtsilä': '#FF6600',
  'Rolls-Royce': '#281432',
  'John Deere': '#367C2B',
};

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredEngines, setFeaturedEngines] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [readyToShip, setReadyToShip] = useState([]);
  const [brandsWithCount, setBrandsWithCount] = useState([]);
  const [stats, setStats] = useState({ total: 0, brands: 0 });
  const [loading, setLoading] = useState(true);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  useEffect(() => {
    fetchData();
    // Hero image rotation
    const interval = setInterval(() => {
      setCurrentHeroIndex(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      await axios.post(`${API_URL}/api/seed-data`);
      
      const [featuredRes, bestsellersRes, readyRes, brandsRes] = await Promise.all([
        axios.get(`${API_URL}/api/engines/featured`),
        axios.get(`${API_URL}/api/engines/bestsellers`),
        axios.get(`${API_URL}/api/engines?is_ready_to_ship=true&limit=8`),
        axios.get(`${API_URL}/api/brands/with-count`)
      ]);
      
      setFeaturedEngines(featuredRes.data);
      setBestsellers(bestsellersRes.data);
      setReadyToShip(readyRes.data.engines || []);
      setBrandsWithCount(brandsRes.data);
      setStats({
        total: brandsRes.data.reduce((acc, b) => acc + b.count, 0),
        brands: brandsRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/inventory?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <main data-testid="home-page" className="bg-slate-50">
      {/* Hero Section - Modern Ecommerce Style */}
      <section className="relative min-h-[600px] lg:min-h-[700px] overflow-hidden" data-testid="hero-section">
        {/* Background Images with Transition */}
        {heroImages.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentHeroIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img src={img} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 container-custom h-full flex items-center py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
            {/* Left Content */}
            <div className="text-white">
              <div className="flex items-center gap-2 mb-6">
                <Badge className="bg-red-500 text-white px-3 py-1">
                  <Zap className="w-3 h-3 mr-1" />
                  {stats.total}+ Engines Ready
                </Badge>
                <Badge variant="outline" className="border-white/30 text-white px-3 py-1">
                  {stats.brands} Top Brands
                </Badge>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Premium Marine
                <span className="text-orange-500"> Diesel Engines</span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 max-w-lg">
                The world's largest inventory of new, rebuilt, and quality used marine diesel engines. 
                From workboats to superyachts.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex gap-3 mb-8 max-w-lg" data-testid="hero-search">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by brand, model, HP..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 py-6 text-lg bg-white/10 border-white/20 text-white placeholder:text-slate-400 backdrop-blur-sm"
                  />
                </div>
                <Button type="submit" className="btn-primary px-8 py-6">
                  Search
                </Button>
              </form>

              {/* Quick Links */}
              <div className="flex flex-wrap gap-3">
                <Link to="/inventory?condition=New">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-400" />
                    New Engines
                  </Button>
                </Link>
                <Link to="/inventory?condition=Rebuilt">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    <Shield className="w-4 h-4 mr-2 text-blue-400" />
                    Rebuilt
                  </Button>
                </Link>
                <Link to="/inventory?is_ready_to_ship=true">
                  <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Truck className="w-4 h-4 mr-2" />
                    Ready to Ship
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Featured Engine Showcase Carousel */}
            <div className="hidden lg:block">
              <Carousel 
                className="w-full max-w-md ml-auto"
                opts={{ loop: true }}
                plugins={[Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]}
              >
                <CarouselContent>
                  {showcaseEngines.map((engine, idx) => (
                    <CarouselItem key={idx}>
                      <div className="bg-white rounded-sm shadow-2xl overflow-hidden">
                        <div className="relative">
                          <img
                            src={engine.image}
                            alt={engine.title}
                            className="w-full h-72 object-cover"
                          />
                          <Badge className="absolute top-4 left-4 bg-orange-500">Featured</Badge>
                          <Badge className="absolute top-4 right-4 bg-emerald-500">{engine.condition}</Badge>
                        </div>
                        <div className="p-6">
                          <p className="text-sm text-orange-500 font-semibold mb-1">{engine.brand}</p>
                          <h3 className="text-xl font-bold text-slate-900 mb-3">{engine.title}</h3>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-slate-900">{engine.price}</span>
                            <Link to="/inventory">
                              <Button className="btn-primary">View Details</Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>

        {/* Hero Image Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentHeroIndex ? 'bg-orange-500 w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white border-b border-slate-200 py-6">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Globe className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Worldwide Shipping</p>
                <p className="text-sm text-slate-500">Global delivery available</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Quality Guaranteed</p>
                <p className="text-sm text-slate-500">Tested & certified</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Fast Turnaround</p>
                <p className="text-sm text-slate-500">Quick processing</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Headphones className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Expert Support</p>
                <p className="text-sm text-slate-500">Technical assistance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Ship - RED Section */}
      {readyToShip.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-red-600 to-red-700" data-testid="ready-to-ship">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Truck className="w-10 h-10 text-white" />
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
                  </span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Ready to Ship Now</h2>
                  <p className="text-red-200">In stock and available for immediate delivery</p>
                </div>
              </div>
              <Link to="/inventory?is_ready_to_ship=true" className="hidden md:flex items-center gap-2 text-white font-medium hover:gap-3 transition-all">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <Carousel 
              className="w-full" 
              opts={{ align: 'start', loop: true }}
              plugins={[Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })]}
            >
              <CarouselContent className="-ml-4">
                {readyToShip.map((engine) => (
                  <CarouselItem key={engine.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <Link to={`/engine/${engine.id}`} className="block bg-white rounded-sm overflow-hidden group hover:shadow-2xl transition-all">
                      <div className="relative aspect-video">
                        <img
                          src={engine.images?.[0] || heroImages[0]}
                          alt={engine.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <Badge className="absolute top-3 left-3 bg-red-500">Ready to Ship</Badge>
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-orange-500 font-semibold">{engine.brand} • {engine.model}</p>
                        <h3 className="font-semibold text-slate-900 line-clamp-1 group-hover:text-orange-500 transition-colors">
                          {engine.horsepower?.toLocaleString()}HP {engine.condition}
                        </h3>
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-bold text-lg">${engine.price?.toLocaleString()}</span>
                          <span className="text-sm text-orange-500">View →</span>
                        </div>
                      </div>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-0 bg-white/90" />
              <CarouselNext className="right-0 bg-white/90" />
            </Carousel>
          </div>
        </section>
      )}

      {/* Featured Engines */}
      {featuredEngines.length > 0 && (
        <section className="py-16 bg-white" data-testid="featured-engines">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">Featured Engines</h2>
                <p className="text-slate-600">Hand-picked selections from our inventory</p>
              </div>
              <Link to="/inventory?is_featured=true" className="hidden md:flex items-center gap-2 text-orange-500 font-medium hover:gap-3 transition-all">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {featuredEngines.slice(0, 8).map((engine) => (
                <EngineCard key={engine.id} engine={engine} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Brands Section - Modern Dynamic Design */}
      {brandsWithCount.length > 0 && (
        <section className="py-20 relative overflow-hidden" data-testid="brands-section">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600"></div>
          
          {/* Floating elements for dynamic effect */}
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-20 w-40 h-40 bg-yellow-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/5 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute top-20 right-1/4 w-20 h-20 bg-orange-300/20 rounded-full blur-2xl animate-bounce"></div>
          
          <div className="container-custom relative z-10">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-4">
                Trusted Manufacturers
              </span>
              <h2 className="text-4xl font-bold text-white mb-4">Shop by Brand</h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto">
                We partner with the world&apos;s leading marine engine manufacturers to bring you quality and reliability
              </p>
            </div>
            
            {/* Brand cards with glass morphism */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5">
              {brandsWithCount.map((brandData, index) => {
                const brand = brandData.brand || brandData.name;
                const count = brandData.count;
                const brandColor = brandColors[brand] || '#f97316';
                const logoUrl = brandData.logo_url;
                const initials = brand.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                
                return (
                  <Link
                    key={brand}
                    to={`/inventory?brand=${encodeURIComponent(brand)}`}
                    className="group relative bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Shine effect on hover */}
                    <div className="absolute inset-0 rounded-xl overflow-hidden">
                      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                    </div>
                    
                    <div className="relative text-center">
                      {logoUrl ? (
                        <div className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-4 bg-white shadow-lg group-hover:shadow-xl transition-shadow overflow-hidden">
                          <img 
                            src={logoUrl} 
                            alt={`${brand} logo`} 
                            className="w-full h-full object-contain p-2"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<span class="text-2xl font-bold" style="color: ${brandColor}">${initials}</span>`;
                            }}
                          />
                        </div>
                      ) : (
                        <div 
                          className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-shadow"
                          style={{ backgroundColor: brandColor }}
                        >
                          <span className="text-2xl font-bold text-white">{initials}</span>
                        </div>
                      )}
                      <h3 className="font-bold text-white text-lg mb-1 group-hover:text-yellow-300 transition-colors">{brand}</h3>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-white/70 text-sm">{count} engines</span>
                        <ArrowRight className="w-3 h-3 text-white/50 group-hover:text-yellow-300 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            
            {/* CTA Button */}
            <div className="text-center mt-10">
              <Link to="/inventory">
                <Button className="bg-white text-orange-600 hover:bg-yellow-300 hover:text-orange-700 font-semibold px-8 py-3 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                  View All Brands
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
      {/* Bestsellers Carousel */}
      {bestsellers.length > 0 && (
        <section className="py-16 bg-slate-50" data-testid="bestsellers">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-orange-500 fill-orange-500" />
                <div>
                  <h2 className="text-3xl font-bold text-slate-900">Bestsellers</h2>
                  <p className="text-slate-600">Our most popular marine engines</p>
                </div>
              </div>
              <Link to="/inventory?is_bestseller=true" className="hidden md:flex items-center gap-2 text-orange-500 font-medium hover:gap-3 transition-all">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <Carousel className="w-full" opts={{ align: 'start' }}>
              <CarouselContent className="-ml-4">
                {bestsellers.map((engine) => (
                  <CarouselItem key={engine.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <EngineCard engine={engine} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-left-4" />
              <CarouselNext className="-right-4" />
            </Carousel>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="cta-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Need Help Finding the Right Engine?
              </h2>
              <p className="text-xl text-slate-400 mb-8">
                Our marine engine specialists are standing by to help you find the perfect powerplant for your vessel.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary text-lg w-full sm:w-auto">
                    <Phone className="w-5 h-5 mr-2" />
                    Request a Quote
                  </Button>
                </Link>
                <Link to="/inventory">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg w-full sm:w-auto">
                    Browse Inventory
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-slate-800 rounded-sm p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Quick Contact</h3>
                <div className="space-y-4">
                  <a href="tel:+12012344851" className="flex items-center gap-4 text-slate-300 hover:text-orange-500 transition-colors">
                    <Phone className="w-6 h-6" />
                    <div>
                      <p className="text-sm text-slate-500">Call Us</p>
                      <p className="text-lg font-semibold">+1 (201) 234-4851</p>
                    </div>
                  </a>
                  <div className="border-t border-slate-700 pt-4">
                    <p className="text-slate-400 text-sm">Business Hours</p>
                    <p className="text-white">Mon - Fri: 8AM - 6PM EST</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
